import React, { useState } from "react"
import {
	Grid,
	Typography,
	Button,
	CircularProgress,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Link,
	IconButton,
	Tooltip
} from "@material-ui/core"
import {
	CheckCircle as CompletedIcon,
	Replay as RefreshIcon
} from "@material-ui/icons"
import { Alert } from "@material-ui/lab"

import { ErrorType } from "@/hooks/useValidation"

import { ActionDialog, Loading } from "@/components"

import ChannelMigrationService, { LastMigrationResult, MappedChannelMigrationErrorCode, OperationProgressChunk, PreconditionType } from "@/services/ChannelMigration"
import ErrorHandlerService from "@/services/ErrorHandler"

import CardSkeleton from "@/skeletons/CardSkeleton"

import newColors from "@/styles/newColors"

type MigrationState = {
	operationState: Record<OperationProgressChunk["type"], Pick<OperationProgressChunk, "status" | "operationType" | "count">>
	status: "pending" | "loading" | LastMigrationResult["status"]
}

type InstanceChannelMigrationProps = {
	instanceId: number
}

const MIGRATION_STATE_STATUS_TO_DIALOG_PROPS: Record<MigrationState["status"], {
	saveText: string,
	hideSaveButton: boolean,
	hideCancelButton: boolean,
	loading: boolean
}> = {
	loading: {
		saveText: "",
		hideSaveButton: true,
		hideCancelButton: true,
		loading: false
	},
	pending: {
		saveText: "Iniciar Migração",
		hideSaveButton: false,
		hideCancelButton: false,
		loading: false
	},
	processing: {
		saveText: "Realizando Migração",
		hideSaveButton: true,
		hideCancelButton: true,
		loading: true
	},
	success: {
		saveText: "",
		hideSaveButton: true,
		hideCancelButton: true,
		loading: false
	},
	failed: {
		saveText: "Tentar Novamente",
		hideSaveButton: false,
		hideCancelButton: false,
		loading: false
	}
}

const OPERATION_PROGRESS_TYPE_TO_PROGRESS_ITEM: Record<OperationProgressChunk["type"], { label: string }> = {
	instance: {
		label: "Instância"
	},
	channel: {
		label: "Canal"
	},
	contact: {
		label: "Contatos"
	}
}

const OPERATION_PROGRESS_STATUS_TO_ICON: Record<OperationProgressChunk["status"], JSX.Element> = {
	"on-progress": <CircularProgress size={20} />,
	completed: <CompletedIcon htmlColor={newColors.green[500]} />
}

const PRECONDITION_TYPE_TO_LABEL: Record<PreconditionType, string | null> = {
	"empty-message-queue": null,
	"no-active-integration": null,
	"whatsapp-channel-with-no-session": null,
	"no-pending-message-blast-without-template-message": "Envios em massa ativos sem mensagem de template",
	"no-active-bot-without-template-block": "Bots ativos sem bloco de template",
	"no-scheduled-snooze-without-template-message": "Chats com soneca ativo sem mensagem de template",
	"no-existing-integration": "Integrações descontinuadas"
}

const ERROR_CODE_TO_ERROR_DESCRIPTION: Record<MappedChannelMigrationErrorCode | "default", string> = {
	InboxChannelIsNotWhatsappType: "O canal precisa ser da API Web para ser migrado para a API WABA.",
	default: "Tente novamente em alguns minutos."
}

const InstanceChannelMigration: React.FC<InstanceChannelMigrationProps> = (props) => {
	const { instanceId } = props

	const [openDialog, setOpenDialog] = useState(false)
	const [isLoadingLastMigrationResult, setIsLoadingLastMigrationResult] = useState(true)
	const [isLoadingAllData, setIsLoadingAllData] = useState(true)
	const [operationProgressChunks, setOperationProgressChunks] = useState<OperationProgressChunk[]>([])
	const [lastMigrationResult, setLastMigrationResult] = useState({} as LastMigrationResult)

	const handleUpdateLastMigrationResult = (data: Partial<LastMigrationResult>) => {
		setLastMigrationResult(lastState => ({
			...lastState,
			...data
		}))
	}

	const handleUpdateOperationProgressChunks = async (chunk: OperationProgressChunk) => {
		setOperationProgressChunks(lastState => ([
			...lastState,
			chunk
		]))
	}

	const handleConsolidateMigrationState = (): MigrationState => {
		const migrationState: MigrationState = {
			operationState: {
				instance: {
					count: 0,
					operationType: "migration",
					status: "on-progress"
				},
				channel: {
					count: 0,
					operationType: "migration",
					status: "on-progress"
				},
				contact: {
					count: 0,
					operationType: "migration",
					status: "on-progress"
				}
			},
			status: "pending"
		}

		operationProgressChunks.forEach(operationProgressChunk => {
			const isMigrationOperation = operationProgressChunk.operationType === "migration"
			const isThereAnyMappedOperationProgress = Boolean(migrationState.operationState[operationProgressChunk.type])

			if (isMigrationOperation && isThereAnyMappedOperationProgress) {
				migrationState.operationState[operationProgressChunk.type].count += operationProgressChunk.count
				migrationState.operationState[operationProgressChunk.type].status = operationProgressChunk.status
				migrationState.operationState[operationProgressChunk.type].operationType = operationProgressChunk.operationType
			}
		})

		const isMigrationOperation = lastMigrationResult.operationType === "migration"
		const isThereAnyExistingStatus = Boolean(lastMigrationResult.status)

		if (isMigrationOperation && isThereAnyExistingStatus) {
			migrationState.status = lastMigrationResult.status
		}

		if (isLoadingAllData) {
			migrationState.status = "loading"
		}

		return migrationState
	}

	const handleMigrate = async () => {
		try {
			handleUpdateLastMigrationResult({ operationType: "migration", status: "processing" })

			const {
				success,
				migrationResult
			} = await ChannelMigrationService.runOperation(instanceId, "migration", handleUpdateOperationProgressChunks)

			if (migrationResult) {
				handleUpdateLastMigrationResult(migrationResult)
			}

			handleUpdateLastMigrationResult({ status: success ? "success" : "failed" })
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
			handleUpdateLastMigrationResult({ status: "failed" })
		}
	}

	const handleLoadLastMigrationResult = async () => {
		try {
			setIsLoadingLastMigrationResult(true)

			const lastResult = await ChannelMigrationService.fetchLastResult(instanceId)

			if (lastResult) {
				setLastMigrationResult(lastResult)
			} else {
				setLastMigrationResult({} as LastMigrationResult)
			}
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		} finally {
			setIsLoadingLastMigrationResult(false)
		}
	}

	const handleOpen = async () => {
		setIsLoadingAllData(true)
		setOpenDialog(true)

		await handleLoadLastMigrationResult()

		setIsLoadingAllData(false)
	}

	const handleClose = () => {
		setOpenDialog(false)
	}

	const migrationState = handleConsolidateMigrationState()

	const handleGetDialogContentByMigrationStateStatus = (migrationStateStatus: MigrationState["status"]) => {
		const migrationStateStatusToDialogContent: Record<MigrationState["status"], JSX.Element> = {
			loading: (
				<Loading
					customLoadingElement={(
						<CardSkeleton
							cardHeight={200}
						/>
					)}
					loading={isLoadingAllData}
				/>
			),
			pending: (
				<Grid container direction="column" spacing={1}>
					<Grid item>
						<Grid container direction="column" spacing={1}>
							<Grid item>
								<Alert severity="warning">
									As ações abaixo serão executadas automaticamente ao iniciar a migração.
								</Alert>
							</Grid>
							<Grid item>
								<List dense>
									<ListItem>
										<ListItemText primary="- O canal será desconectado" />
									</ListItem>
									<ListItem>
										<ListItemText primary="- A fila de mensagens será limpa" />
									</ListItem>
									<ListItem>
										<ListItemText primary="- As integrações ativas serão desativadas" />
									</ListItem>
								</List>
							</Grid>
						</Grid>
					</Grid>
					<Grid item>
						<Typography variant="body2" color="textSecondary">
							O canal será migrado da API Web para a API WABA, com preservação integral de todos os dados existentes, incluindo contatos, conversas, mensagens, bots, tags, etc.
						</Typography>
					</Grid>
				</Grid>
			),
			processing: (
				<Grid container direction="column" spacing={2}>
					<Grid item>
						<Alert severity="info">
							Não feche este modal até a operação ser concluída.
						</Alert>
					</Grid>
					<Grid item>
						<List>
							{Object.entries(OPERATION_PROGRESS_TYPE_TO_PROGRESS_ITEM).map(([operationProgressType, progressItem]) => {
								const operationState = migrationState.operationState[operationProgressType as OperationProgressChunk["type"]]

								return (
									<ListItem key={operationProgressType}>
										<ListItemIcon>
											{OPERATION_PROGRESS_STATUS_TO_ICON[operationState.status]}
										</ListItemIcon>
										<ListItemText
											primary={progressItem.label}
											secondary={operationState.count > 1 ? `${operationState.count} items processados` : null}
										/>
									</ListItem>
								)
							})}
						</List>
					</Grid>
				</Grid>
			),
			success: (
				<Grid container direction="column" spacing={2}>
					<Grid item>
						<Alert
							severity="success"
							action={(
								<IconButton
									onClick={handleLoadLastMigrationResult}
									size="small"
								>
									<Tooltip
										title="Checar pendências novamente"
									>
										{isLoadingLastMigrationResult ? (
											<CircularProgress size={24} color="secondary" />
										) : (
											<RefreshIcon />
										)}
									</Tooltip>
								</IconButton>
							)}
						>
							A migração foi concluída com sucesso.
						</Alert>
					</Grid>
					<Grid item>
						<Grid container direction="column">
							<Grid item>
								<Typography variant="subtitle1">
									<strong>Pendências para revisão manual:</strong>
								</Typography>
							</Grid>
							<Grid item>
								{lastMigrationResult.stillPendingPreconditions?.length === 0 ? (
									<Typography variant="body2" color="textSecondary">
										Nenhum item pendente para ser resolvido manualmente.
									</Typography>
								) : (
									<List dense>
										{lastMigrationResult.stillPendingPreconditions?.map((item, index) => (
											<ListItem key={index} alignItems="flex-start" disableGutters>
												<ListItemText
													primary={PRECONDITION_TYPE_TO_LABEL[item.type] || "Outros"}
													secondary={
														item.pendingResourceUrls.length > 0 && (
															<Grid container direction="column">
																{item.pendingResourceUrls.map((url, urlIndex) => (
																	<Grid item key={urlIndex}>
																		<Link href={url} target="_blank" rel="noopener noreferrer" variant="body2">
																			- {url}
																		</Link>
																	</Grid>
																))}
															</Grid>
														)
													}
												/>
											</ListItem>
										))}
									</List>
								)}
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			),
			failed: (
				<Grid container direction="column">
					<Grid item>
						<Alert severity="error">
							Ocorreu um erro ao tentar iniciar a migração:{" "}
							{ERROR_CODE_TO_ERROR_DESCRIPTION[lastMigrationResult?.errorCode as MappedChannelMigrationErrorCode] || ERROR_CODE_TO_ERROR_DESCRIPTION.default}
						</Alert>
					</Grid>
				</Grid>
			)
		}

		return migrationStateStatusToDialogContent[migrationStateStatus]
	}

	const dialogContent = handleGetDialogContentByMigrationStateStatus(migrationState.status)

	return (
		<>
			<Button onClick={handleOpen}>
				MIGRAR CANAL DA API-WEB PARA API-WABA
			</Button>

			<ActionDialog
				fullWidth
				maxWidth="sm"
				title={`Migração de Canal da Instância: ${instanceId}`}
				openDialog={openDialog}
				onClose={handleClose}
				onSave={handleMigrate}
				{...MIGRATION_STATE_STATUS_TO_DIALOG_PROPS[migrationState.status]}
			>
				{dialogContent}
			</ActionDialog>
		</>
	)
}

export default InstanceChannelMigration
